import { useEffect, useMemo, useState } from 'react';

import { CheckIcon, Globe } from 'lucide-react';
import { Button, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, LoadingSpinner, Popover, PopoverContent, PopoverTrigger } from 'twyne-new.ui'

import appStore from '@/stores/app';
import { APP_PREFIX } from '@/utils/constants';

const SiteItem = ({ active, name, value, onSelect }) => <>
  <CommandItem asChild
    value={value}
    onSelect={onSelect}
  >
    <Button variant='ghost' size='md' color={active ? 'accent' : 'neutral'} block className='justify-start'>
    <div className='relative'>
        {name}
        {active ? <CheckIcon size={14} className='absolute -right-4 top-0' /> : null}
      </div>
    </Button>
  </CommandItem>
</>

export const SiteList = ({ sites, selected, loading, onSelect }) => <>
  <Command>
    <CommandInput placeholder='Search site...' />
    <CommandList>
      {loading && <div className='flex items-center justify-center py-6'>
        <LoadingSpinner width={18} height={18} />
      </div>}
      {!loading && <>
        <CommandEmpty>No site found.</CommandEmpty>
        <CommandGroup>
          {sites.map(site => <SiteItem key={site.id}
            value={site.id}
            name={site.name}
            active={selected?.id === site.id}
            onSelect={() => onSelect(site)}
          />)}
        </CommandGroup>
      </>}
    </CommandList>
  </Command>
</>

export default function SiteSelection() {
  const [open, setOpen] = useState(false)
  const [sites, setSites] = useState([])
  const [loading, setLoading] = useState(true)

  const selected = useMemo(() => appStore.site && {
    id: appStore.site.id,
    name: sites.find(site => site.id === appStore.site.id)?.name,
  }, [sites]);

  const loadData = async () => {
    try {
      const sites = await appStore.listSites()
      setSites(sites.map(({ name, id }) => ({
        name: `[#${id}] ${name}`,
        id,
      })))
    } finally {
      setLoading(false)
    }
  }

  const handleSelectSite = site => {
    setOpen(false);

    const pathname = document.location.pathname
      .replace(new RegExp(`^${APP_PREFIX}/\\d+`), '')
      .replace(/\/(add|\d+).*$/, '') || '/'

    appStore.setSiteById(site.id);
    document.location.assign(`${APP_PREFIX}/${site.id}${pathname}`)
  }

  useEffect(() => {
    loadData();
  }, [])

  return <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant='outline' color='accent' role='combobox' aria-expanded={open} className='hover:border-blue-600 hover:bg-blue-400/20! hover:text-blue-300'>
          <Globe size={14} />
          {selected?.name || 'Select the site'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='p-0'>
        <SiteList sites={sites} selected={selected} loading={loading} onSelect={handleSelectSite} />
      </PopoverContent>
    </Popover>
  </>
}
